import React from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Mic,
  Play,
  Pause,
  Square,
  Rewind,
  Save,
  Download,
  Eye,
  Pencil,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import {
  parseLrc,
  buildLrc,
  scaleLrcLines,
  activeLineIndex,
} from "../lib/lrc";
import { TranscriptionLive } from "./TranscriptionLive";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * KaraokeDialog
 * - Mode "Préparation": load lyrics text (paste or PDF auto-extract), preview lines.
 * - Mode "Enregistrement": play the MP3, tap Space / button to record per-line timestamps.
 * - Mode "Concert": auto-scroll 3-line teleprompter at recorded speed × user multiplier.
 *
 * Speed multiplier is persisted per song (karaoke_speed). The recorded LRC is
 * persisted too (karaoke_lrc) and never mutated by the speed slider — we always
 * scale on the fly for playback so the user can experiment without losing data.
 */
export const KaraokeDialog = ({
  open,
  onOpenChange,
  song,
  onSongUpdated,
  // Shared audio state driven by the parent's <audio> element. Using the same
  // player means: (a) one source of truth so timestamps are always against the
  // actual playback time, (b) the dialog inherits the variant (Répète /
  // Playback / Live) chosen on the main page, and (c) no double-buffering
  // issues that previously left the dialog's Play button inactive.
  audioPlaying = false,
  audioCurrentTime = 0,
  audioDuration = 0,
  audioReady = false,
  onTogglePlay,
  onSeek,
}) => {
  // 'edit' = paste/load lyrics. 'record' = tap-along to capture timestamps.
  // 'play' = concert playback. We stay in 'play' once an LRC exists.
  const [mode, setMode] = React.useState("edit");
  const [lyricsText, setLyricsText] = React.useState(""); // \n-separated lines (no timing)
  const [recordedLines, setRecordedLines] = React.useState([]); // [{time, text}]
  const [speed, setSpeed] = React.useState(1.0);
  const [busy, setBusy] = React.useState(false);

  // Audio playback — driven by the parent's <audio>. Aliases for readability.
  const isPlaying = audioPlaying;
  const currentTime = audioCurrentTime;

  // Recording state: the index of the next line that will receive a timestamp
  const [recordPointer, setRecordPointer] = React.useState(0);
  // Pending list = the parsed lines from `lyricsText` waiting to be timed
  const pendingLines = React.useMemo(
    () =>
      lyricsText
        .replace(/\r/g, "")
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
    [lyricsText]
  );

  // ----- Hydrate when opening / song changes -----
  // Effect-driven initialisation of the local edit buffers from the `song`
  // prop. Intentionally only re-runs when `open` or `song.id` change so the
  // user's in-progress edits aren't wiped by unrelated parent re-renders.
  /* eslint-disable react-hooks/exhaustive-deps */
  React.useEffect(() => {
    if (!open || !song) return;
    setSpeed(song.karaoke_speed && song.karaoke_speed > 0 ? song.karaoke_speed : 1.0);
    if (song.karaoke_lrc && song.karaoke_lrc.trim()) {
      const parsed = parseLrc(song.karaoke_lrc);
      setRecordedLines(parsed.lines);
      setLyricsText(parsed.lines.map((l) => l.text).join("\n"));
      setMode("play");
    } else {
      setRecordedLines([]);
      setLyricsText("");
      setMode("edit");
    }
    setRecordPointer(0);
  }, [open, song?.id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // ----- Audio info derived from song -----
  const audioName =
    song?.audio_playback_filename ||
    song?.audio_repete_filename ||
    song?.audio_filename;
  const hasAudio = !!audioName;

  // No internal audio element / event listeners — the parent owns playback.
  // Pause when dialog closes is handled by the parent through onTogglePlay if needed.

  const togglePlay = () => {
    onTogglePlay?.();
  };

  const seek = (t) => {
    if (typeof onSeek === "function") onSeek(t);
  };

  // ----- Recording: tap to capture next line -----
  const tapNextLine = React.useCallback(() => {
    if (mode !== "record") return;
    // Use parent's audio time (single source of truth)
    const t = audioCurrentTime || 0;
    const text = pendingLines[recordPointer];
    if (text === undefined) return;
    setRecordedLines((prev) => {
      const next = [...prev];
      next[recordPointer] = { time: t, text };
      return next;
    });
    setRecordPointer((p) => p + 1);
  }, [mode, recordPointer, pendingLines, audioCurrentTime]);

  const undoLastTap = () => {
    if (recordPointer === 0) return;
    setRecordPointer((p) => p - 1);
    setRecordedLines((prev) => prev.slice(0, -1));
  };

  const resetRecording = () => {
    if (recordedLines.length > 0 && !window.confirm("Effacer tous les timestamps enregistrés ?")) return;
    setRecordedLines([]);
    setRecordPointer(0);
    seek(0);
  };

  // ===== Calibration helpers (manual editing of timestamps) =====
  const parseTsInput = (raw) => {
    if (!raw) return null;
    const s = String(raw).trim();
    if (!s) return null;
    if (/^\d+(\.\d+)?$/.test(s)) return parseFloat(s);
    const m = s.match(/^(\d{1,2}):(\d{2}(?:\.\d+)?)$/);
    if (m) return parseInt(m[1], 10) * 60 + parseFloat(m[2]);
    return null;
  };
  const formatTs = (t) => {
    if (t === null || t === undefined) return "";
    const mm = Math.floor(t / 60);
    const rest = t - mm * 60;
    return `${String(mm).padStart(2, "0")}:${rest.toFixed(2).padStart(5, "0")}`;
  };

  const patchLineTime = (idx, newTime) => {
    const text = pendingLines[idx];
    if (text === undefined) return;
    setRecordedLines((prev) => {
      const next = prev.slice();
      next[idx] = { time: Math.max(0, newTime || 0), text };
      return next;
    });
  };

  const nudgeLine = (idx, delta) => {
    const cur = recordedLines[idx]?.time ?? 0;
    patchLineTime(idx, Math.max(0, cur + delta));
  };

  // Shift ALL stamped timestamps by `delta` seconds (positive or negative).
  // Lines that haven't been stamped yet are left untouched.
  const shiftAll = (delta) => {
    setRecordedLines((prev) =>
      prev.map((l) =>
        l && typeof l.time === "number"
          ? { ...l, time: Math.max(0, l.time + delta) }
          : l
      )
    );
  };

  // STAMP — capture current audio time onto the *selected* line then advance
  // to the next unstamped line (or +1 if all are stamped).
  const stampLine = React.useCallback(
    (forcedIdx) => {
      const idx = typeof forcedIdx === "number" ? forcedIdx : recordPointer;
      const text = pendingLines[idx];
      if (text === undefined) return;
      const t = audioCurrentTime || 0;
      setRecordedLines((prev) => {
        const next = prev.slice();
        next[idx] = { time: t, text };
        return next;
      });
      setRecordPointer((p) => {
        const n = pendingLines.length;
        let q = (typeof forcedIdx === "number" ? forcedIdx : p) + 1;
        while (
          q < n &&
          recordedLines[q] &&
          typeof recordedLines[q].time === "number"
        ) {
          q += 1;
        }
        return Math.min(q, n - 1) >= 0 ? Math.min(q, n - 1) : 0;
      });
    },
    [recordPointer, pendingLines, audioCurrentTime, recordedLines]
  );

  // Keyboard: Space = tap line during recording; Space also taps in play mode for manual skip
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "textarea" || tag === "input") return;
      if (e.code === "Space") {
        e.preventDefault();
        if (mode === "record") stampLine();
        else if (mode === "play") {
          if (recordedLines.length === 0) return;
          const scaled = scaleLrcLines(recordedLines, speed);
          const i = activeLineIndex(scaled, audioCurrentTime || 0);
          if (i + 1 < scaled.length) {
            seek(scaled[i + 1].time + 0.01);
          }
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, mode, stampLine, recordedLines, speed, audioCurrentTime]);

  // ----- Web MIDI input (any note-on triggers the same as Space in record mode) -----
  React.useEffect(() => {
    if (!open || mode !== "record") return;
    if (typeof navigator === "undefined" || !navigator.requestMIDIAccess) return;
    let access = null;
    let inputs = [];
    const onMidiMsg = (e) => {
      // Note-on (status nibble 0x9, non-zero velocity) on any channel
      const status = e.data?.[0];
      const velocity = e.data?.[2] ?? 0;
      if (status !== undefined && (status & 0xf0) === 0x90 && velocity > 0) {
        stampLine();
      }
    };
    navigator
      .requestMIDIAccess({ sysex: false })
      .then((ma) => {
        access = ma;
        ma.inputs.forEach((inp) => {
          inputs.push(inp);
          inp.onmidimessage = onMidiMsg;
        });
      })
      .catch(() => {});
    return () => {
      inputs.forEach((inp) => { try { inp.onmidimessage = null; } catch (_) { /* noop */ } });
      access = null;
    };
  }, [open, mode, stampLine]);

  // ----- Persistence -----
  const saveLrc = async () => {
    if (!song) return;
    setBusy(true);
    try {
      // Only persist lines that have a valid timestamp. Order by time so the
      // LRC reads chronologically (in case the user re-stamped out of order).
      const ordered = recordedLines
        .filter((l) => l && typeof l.time === "number")
        .sort((a, b) => a.time - b.time);
      const lrc = buildLrc(ordered, {
        ti: song.title,
        ar: song.composer || song.artist || "",
        al: "GiGLive26",
      });
      const { data } = await axios.patch(`${API}/songs/${song.id}`, {
        karaoke_lrc: lrc,
        karaoke_speed: speed,
      });
      onSongUpdated?.(data);
      toast.success("Synchro karaoké enregistrée");
      setMode("play");
    } catch (e) {
      toast.error("Échec de l'enregistrement");
    } finally {
      setBusy(false);
    }
  };

  const persistSpeed = React.useCallback(
    async (newSpeed) => {
      if (!song) return;
      try {
        const { data } = await axios.patch(`${API}/songs/${song.id}`, {
          karaoke_speed: newSpeed,
        });
        onSongUpdated?.(data);
      } catch (_) {
        /* silent; the value stays in memory */
      }
    },
    [song, onSongUpdated]
  );

  const onSpeedChange = (v) => {
    setSpeed(v);
  };
  const onSpeedCommit = (v) => {
    setSpeed(v);
    persistSpeed(v);
  };

  const exportLrc = () => {
    if (!song) return;
    const token = window.localStorage.getItem("gl26_auth_token");
    const url = `${API}/songs/${song.id}/karaoke/export.lrc?t=${encodeURIComponent(token || "")}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = "";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const autoExtractPdf = async () => {
    if (!song?.pdf_filename) {
      toast.message("Aucun PDF chargé pour ce morceau", {
        description: "Tu peux coller le texte des paroles à la main.",
      });
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/songs/${song.id}/karaoke/extract-pdf-text`);
      const lines = (data?.lines || []).filter(Boolean);
      if (lines.length === 0) {
        toast.warning("Aucun texte extrait du PDF — colle-le à la main");
      } else {
        setLyricsText(lines.join("\n"));
        toast.success(`${lines.length} lignes extraites du PDF`);
      }
    } catch (e) {
      toast.error("Extraction PDF impossible — colle le texte à la main");
    } finally {
      setBusy(false);
    }
  };

  // ----- Scaled timeline for playback mode -----
  const scaledLines = React.useMemo(
    () => scaleLrcLines(recordedLines, speed),
    [recordedLines, speed]
  );
  const activeIdx = React.useMemo(
    () => activeLineIndex(scaledLines, currentTime),
    [scaledLines, currentTime]
  );

  // ----- Render -----
  const hasLrc = recordedLines.length > 0;
  const recordProgress =
    pendingLines.length > 0 ? recordPointer / pendingLines.length : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0d0d0d] border-white/10 text-white max-w-3xl max-h-[92vh] overflow-y-auto no-scrollbar"
        data-testid="karaoke-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-lg text-[var(--gl-gold)] font-display flex items-center gap-2">
            <Sparkles size={18} /> Transcription Live · {song?.id}
          </DialogTitle>
          <DialogDescription className="text-neutral-400 text-xs">
            Téléprompteur de scène. Édite les paroles, cale les timestamps (Espace ou pédale MIDI), puis bascule en <b>Transcription Live</b>.
          </DialogDescription>
        </DialogHeader>

        {/* Mode switcher */}
        <div className="grid grid-cols-3 gap-1.5">
          <button
            onClick={() => setMode("edit")}
            className={`px-2 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider border transition flex items-center justify-center gap-1.5 ${
              mode === "edit"
                ? "bg-[var(--gl-gold)] text-black border-[var(--gl-gold)]"
                : "bg-black text-white/70 border-white/10 hover:border-white/30"
            }`}
            data-testid="karaoke-tab-edit"
          >
            <Pencil size={12} /> Paroles
          </button>
          <button
            onClick={() => setMode("record")}
            disabled={pendingLines.length === 0 || !hasAudio}
            className={`px-2 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider border transition flex items-center justify-center gap-1.5 disabled:opacity-40 ${
              mode === "record"
                ? "bg-red-500 text-black border-red-500"
                : "bg-black text-red-400 border-red-500/40 hover:border-red-500"
            }`}
            data-testid="karaoke-tab-record"
          >
            <Mic size={12} /> Enregistrer
          </button>
          <button
            onClick={() => setMode("play")}
            disabled={!hasLrc}
            className={`px-2 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider border transition flex items-center justify-center gap-1.5 disabled:opacity-40 ${
              mode === "play"
                ? "bg-[var(--gl-neon)] text-black border-[var(--gl-neon)]"
                : "bg-black text-[var(--gl-neon)] border-[var(--gl-neon)]/40 hover:border-[var(--gl-neon)]"
            }`}
            data-testid="karaoke-tab-play"
          >
            <Eye size={12} /> Transcription
          </button>
        </div>

        {/* Audio is driven by the parent player — no local <audio> element. */}
        {!hasAudio && (
          <div className="rounded-xl border border-dashed border-orange-400/40 bg-orange-400/5 text-orange-300 p-3 text-sm">
            Aucun MP3 chargé pour ce morceau. Utilise « Charger » pour ajouter un audio.
          </div>
        )}

        {/* ============ MODE EDIT ============ */}
        {mode === "edit" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-neutral-400">
                Texte des paroles · {pendingLines.length} ligne{pendingLines.length > 1 ? "s" : ""}
              </div>
              <button
                onClick={autoExtractPdf}
                disabled={busy}
                className="text-[10px] uppercase tracking-widest text-[var(--gl-gold)] hover:brightness-110 disabled:opacity-40 flex items-center gap-1"
                data-testid="karaoke-extract-pdf-btn"
                title="Extraire le texte du PDF déjà chargé"
              >
                <Sparkles size={11} /> {busy ? "Extraction…" : "Extraire du PDF"}
              </button>
            </div>
            <textarea
              value={lyricsText}
              onChange={(e) => setLyricsText(e.target.value)}
              placeholder={"Colle ici le texte des paroles.\nUne ligne par ligne chantée.\n\nLes lignes vides sont ignorées."}
              className="w-full h-64 bg-black border border-white/15 rounded-xl p-3 text-white text-sm leading-relaxed focus:outline-none focus:border-[var(--gl-gold)] resize-none"
              data-testid="karaoke-lyrics-textarea"
            />
            <button
              onClick={() => setMode("record")}
              disabled={pendingLines.length === 0 || !hasAudio}
              className="w-full tap-lg rounded-xl bg-red-500 text-black font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:brightness-110 disabled:opacity-40"
              data-testid="karaoke-start-record-btn"
            >
              <Mic size={16} /> Passer à l&apos;enregistrement
            </button>
          </div>
        )}

        {/* ============ MODE RECORD (calibration grid) ============ */}
        {mode === "record" && (
          <div className="space-y-3">
            {/* Transport row */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => seek(0)}
                className="w-10 h-10 rounded-lg bg-black border border-white/15 flex items-center justify-center hover:border-white/30"
                title="Retour au début"
                data-testid="karaoke-record-reset"
              >
                <Rewind size={16} />
              </button>
              <button
                onClick={togglePlay}
                disabled={!audioReady}
                className="flex-1 tap-lg rounded-lg bg-[var(--gl-gold)] text-black font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="karaoke-record-play"
              >
                {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                {isPlaying ? "Pause" : "Lecture"}
              </button>
              <button
                onClick={undoLastTap}
                disabled={recordedLines.length === 0}
                className="px-3 py-2 rounded-lg bg-black border border-white/15 text-xs disabled:opacity-40"
                data-testid="karaoke-record-undo"
                title="Annuler le dernier stamp"
              >
                Annuler
              </button>
            </div>
            <div className="flex items-center justify-between text-[10px] text-neutral-500 tabular-nums">
              <span>
                {formatTs(currentTime)} ·{" "}
                {recordedLines.filter((l) => l && typeof l.time === "number").length}/
                {pendingLines.length} calées
              </span>
              <span>Cliquez sur une ligne pour la sélectionner</span>
            </div>

            {/* Big STAMP button (works for the currently selected line) */}
            <button
              onClick={() => stampLine()}
              disabled={recordPointer >= pendingLines.length}
              className="w-full rounded-2xl bg-red-500 text-black font-bold uppercase tracking-widest text-lg py-5 flex items-center justify-center gap-3 disabled:opacity-40 hover:brightness-110 active:scale-[0.98] transition"
              data-testid="karaoke-tap-btn"
              title="Espace ou pédale MIDI"
            >
              <Mic size={20} />
              STAMP · {pendingLines[recordPointer] ? `« ${pendingLines[recordPointer].slice(0, 40)}${pendingLines[recordPointer].length > 40 ? "…" : ""} »` : "Toutes calées"}
              <kbd className="text-[10px] bg-black/30 px-2 py-1 rounded">Espace</kbd>
            </button>

            {/* Calibration grid — clickable rows with editable timestamps */}
            <div className="bg-black border border-white/10 rounded-xl overflow-hidden max-h-[40vh] overflow-y-auto no-scrollbar">
              <div className="grid grid-cols-[3rem_5.5rem_1fr_auto] items-center gap-1 px-2 py-1.5 sticky top-0 bg-black border-b border-white/10 text-[9px] uppercase tracking-widest text-neutral-500">
                <span>#</span>
                <span>Timestamp</span>
                <span>Texte</span>
                <span className="text-right pr-1">Ajust.</span>
              </div>
              {pendingLines.map((text, idx) => {
                const stamped = recordedLines[idx];
                const ts = stamped && typeof stamped.time === "number" ? stamped.time : null;
                const isActive = idx === recordPointer;
                return (
                  <div
                    key={idx}
                    className={`grid grid-cols-[3rem_5.5rem_1fr_auto] items-center gap-1 px-2 py-1 border-b border-white/5 transition cursor-pointer ${
                      isActive
                        ? "bg-[var(--gl-gold)]/10 border-l-2 border-l-[var(--gl-gold)]"
                        : "hover:bg-white/5"
                    }`}
                    data-testid={`karaoke-line-${idx}`}
                    onClick={() => setRecordPointer(idx)}
                  >
                    <span className="text-[11px] text-neutral-500 tabular-nums">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <input
                      type="text"
                      defaultValue={ts !== null ? formatTs(ts) : ""}
                      key={`ts-${idx}-${ts ?? "null"}`}
                      onClick={(e) => e.stopPropagation()}
                      onBlur={(e) => {
                        const parsed = parseTsInput(e.currentTarget.value);
                        if (parsed === null) {
                          e.currentTarget.value = ts !== null ? formatTs(ts) : "";
                          return;
                        }
                        patchLineTime(idx, parsed);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.currentTarget.blur();
                      }}
                      placeholder="--:--.--"
                      className={`bg-transparent border border-transparent hover:border-white/15 focus:border-[var(--gl-gold)] focus:outline-none rounded px-1.5 py-0.5 text-[11px] font-bold tabular-nums w-full ${
                        ts !== null ? "text-[var(--gl-gold)]" : "text-neutral-600"
                      }`}
                      data-testid={`karaoke-ts-input-${idx}`}
                    />
                    <span className="text-[12px] text-white/85 truncate">
                      {text}
                    </span>
                    <div className="flex gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => nudgeLine(idx, -0.1)}
                        disabled={ts === null}
                        className="w-6 h-6 rounded border border-white/10 text-[10px] text-white/70 hover:border-[var(--gl-gold)] hover:text-[var(--gl-gold)] disabled:opacity-30"
                        title="Reculer de 0.10 s"
                        data-testid={`karaoke-nudge-back-${idx}`}
                      >
                        −
                      </button>
                      <button
                        onClick={() => nudgeLine(idx, 0.1)}
                        disabled={ts === null}
                        className="w-6 h-6 rounded border border-white/10 text-[10px] text-white/70 hover:border-[var(--gl-gold)] hover:text-[var(--gl-gold)] disabled:opacity-30"
                        title="Avancer de 0.10 s"
                        data-testid={`karaoke-nudge-fwd-${idx}`}
                      >
                        +
                      </button>
                      <button
                        onClick={() => ts !== null && seek(ts)}
                        disabled={ts === null}
                        className="w-6 h-6 rounded border border-white/10 text-[10px] text-white/70 hover:border-[var(--gl-neon)] hover:text-[var(--gl-neon)] disabled:opacity-30"
                        title="Écouter à ce timestamp"
                        data-testid={`karaoke-play-at-${idx}`}
                      >
                        ▶
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Global shift */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => shiftAll(-0.1)}
                disabled={recordedLines.length === 0}
                className="rounded-lg bg-black border border-white/15 text-white py-2 text-[11px] font-bold uppercase tracking-wider hover:border-[var(--gl-gold)]/60 disabled:opacity-40 flex items-center justify-center gap-1.5"
                data-testid="karaoke-shift-all-back"
                title="Reculer TOUS les timestamps de 0,10 s"
              >
                ⟪ −0.10 s tout
              </button>
              <button
                onClick={() => shiftAll(0.1)}
                disabled={recordedLines.length === 0}
                className="rounded-lg bg-black border border-white/15 text-white py-2 text-[11px] font-bold uppercase tracking-wider hover:border-[var(--gl-gold)]/60 disabled:opacity-40 flex items-center justify-center gap-1.5"
                data-testid="karaoke-shift-all-fwd"
                title="Avancer TOUS les timestamps de 0,10 s"
              >
                +0.10 s tout ⟫
              </button>
            </div>

            <div className="flex gap-2">
              <button
                onClick={resetRecording}
                disabled={busy || recordedLines.length === 0}
                className="flex-1 rounded-xl bg-black border border-white/15 text-white py-2.5 text-xs font-bold uppercase tracking-wider hover:border-white/30 disabled:opacity-40"
              >
                Reset
              </button>
              <button
                onClick={saveLrc}
                disabled={busy || recordedLines.length === 0}
                className="flex-1 tap-lg rounded-xl bg-[var(--gl-neon)] text-black font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="karaoke-save-btn"
              >
                <Save size={16} /> {busy ? "Enregistrement…" : "Sauvegarder .LRC"}
              </button>
            </div>
          </div>
        )}

        {/* ============ MODE PLAY = TRANSCRIPTION LIVE ============ */}
        {mode === "play" && (
          <TranscriptionLive
            lines={scaledLines}
            activeIdx={activeIdx}
            currentTime={currentTime}
            audioDuration={audioDuration}
            isPlaying={isPlaying}
            audioReady={audioReady}
            onTogglePlay={togglePlay}
            onSeek={seek}
            speed={speed}
            onSpeedChange={onSpeedChange}
            onSpeedCommit={onSpeedCommit}
            onRedoRecord={() => setMode("record")}
            onExportLrc={exportLrc}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default KaraokeDialog;
